<template>
  <transition name="dialog-scale">
    <div v-if="showDialog" class="dialog-overlay" @click.self="closeDialog">
      <div class="dialog-container">
        <div class="dialog-content">
          <!-- Header -->
          <div class="dialog-header">
            <div class="header-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V12M12 15H12.01M10 3H14C15.1046 3 16 3.89543 16 5V7H8V5C8 3.89543 8.89543 3 10 3ZM4 7H20V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V7Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="header-content">
              <h3 class="dialog-title">Confirm Deletion</h3>
              <p class="dialog-subtitle">This action cannot be undone</p>
            </div>
          </div>

          <!-- Content -->
          <div class="dialog-body">
            <div class="confirmation-card">
              <div class="confirmation-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V12M12 15H12.01M10 3H14C15.1046 3 16 3.89543 16 5V7H8V5C8 3.89543 8.89543 3 10 3ZM4 7H20V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <p class="confirmation-message">
                You are about to permanently delete this item and all associated data. 
                <strong>This action cannot be reversed.</strong> Are you sure you want to proceed?
              </p>
              
              <!-- <div v-if="item && Object.keys(item).length" class="item-details">
                <div class="detail-item">
                  <span class="detail-label">Item Type</span>
                  <span class="detail-value">{{ item.type || 'Unknown' }}</span>
                </div>
                <div v-if="item.name" class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ item.name }}</span>
                </div>
                <div v-if="item.createdAt" class="detail-item">
                  <span class="detail-label">Created</span>
                  <span class="detail-value">{{ formatDate(item.createdAt) }}</span>
                </div>
              </div> -->
            </div>

            <!-- Loading state -->
            <div v-if="loading" class="loading-container">
              <div class="loading-animation">
                <div class="loading-dot dot-1"></div>
                <div class="loading-dot dot-2"></div>
                <div class="loading-dot dot-3"></div>
              </div>
              <span class="loading-text">Rejecting...</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="dialog-actions">
            <button
              class="btn btn-secondary"
              @click="closeDialog"
              :disabled="loading"
              type="button"
            >
              <span class="btn-content">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Cancel
              </span>
            </button>
            <button
              class="btn btn-danger"
              @click="confirm"
              :disabled="loading"
              type="button"
            >
              <span class="btn-content">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-if="!loading">Reject Permanently</span>
                <span v-else>Rejecting...</span>
              </span>
              <span class="btn-shine"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'DeleteDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    showDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    closeDialog() {
      if (!this.loading) {
        this.$emit('close')
      }
    },
    confirm() {
      if (!this.loading) {
        this.$emit('delete')
      }
    },
    handleEscape(event) {
      if (event.key === 'Escape' && this.showDialog && !this.loading) {
        this.closeDialog()
      }
    },
    formatLabel(key) {
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
    },
    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      try {
        return new Date(dateString).toLocaleDateString()
      } catch {
        return dateString
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape)
  }
}
</script>

<style scoped lang="scss">
/* Animations */
@keyframes scaleIn {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}

@keyframes loadingBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

@keyframes shine {
  0% { left: -100%; }
  20% { left: 100%; }
  100% { left: 100%; }
}

/* Transitions */
.dialog-scale-enter-active {
  animation: fadeIn 0.3s ease;
  .dialog-container {
    animation: scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
}

.dialog-scale-leave-active {
  animation: fadeIn 0.3s ease reverse;
  .dialog-container {
    animation: scaleIn 0.3s cubic-bezier(0.6, -0.28, 0.735, 0.045) reverse;
  }
}

/* Overlay */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  perspective: 1000px;
}

/* Container */
.dialog-container {
  width: 100%;
  max-width: 480px;
  margin: auto;
  transform-origin: center center;
}

/* Dialog Content */
.dialog-content {
  background: white;
  border-radius: 20px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: 100%;
  transform-style: preserve-3d;
}

/* Header */
.dialog-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
    transform: rotate(30deg);
  }
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
  flex: 1;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.dialog-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-weight: 400;
}

/* Body */
.dialog-body {
  padding: 24px;
}

.confirmation-card {
  background: #fef2f2;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  border: 1px solid #fecaca;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  }
}

.confirmation-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: white;
  animation: pulse 2s infinite;
}

.confirmation-message {
  font-size: 1.125rem;
  color: #b91c1c;
  line-height: 1.6;
  margin: 0 0 20px 0;
  font-weight: 500;
  
  strong {
    font-weight: 700;
    color: #7f1d1d;
  }
}

/* Item Details */
.item-details {
  background: white;
  border-radius: 12px;
  padding: 0;
  margin: 24px 0 0 0;
  border: 1px solid #fecaca;
  overflow: hidden;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #fee2e2;
  
  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-weight: 500;
  color: #6b7280;
  font-size: 0.875rem;
}

.detail-value {
  color: #111827;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  background: #fef2f2;
  border-radius: 16px;
  margin: 16px 0 0 0;
  border: 1px dashed #fecaca;
}

.loading-animation {
  display: flex;
  gap: 8px;
  height: 24px;
  align-items: flex-end;
}

.loading-dot {
  width: 12px;
  height: 12px;
  background: #dc2626;
  border-radius: 50%;
  
  &.dot-1 {
    animation: loadingBounce 0.8s infinite ease-in-out;
  }
  &.dot-2 {
    animation: loadingBounce 0.8s infinite 0.2s ease-in-out;
  }
  &.dot-3 {
    animation: loadingBounce 0.8s infinite 0.4s ease-in-out;
  }
}

.loading-text {
  font-size: 0.875rem;
  color: #b91c1c;
  font-weight: 500;
  text-align: center;
}

/* Actions */
.dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
}

/* Buttons */
.btn {
  position: relative;
  padding: 12px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  overflow: hidden;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.3);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    transform: none !important;
    box-shadow: none !important;
  }
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.btn-secondary {
  background: white;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.btn-danger {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
  border: none;
  box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.3);
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 15px -3px rgba(220, 38, 38, 0.3);
    animation: pulse 1.5s infinite;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.3);
  }
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: all 0.3s;
}

.btn-danger:hover:not(:disabled) .btn-shine {
  animation: shine 1.5s;
}

/* Responsive Design */
@media (max-width: 640px) {
  .dialog-overlay {
    padding: 1rem;
  }
  
  .dialog-content {
    border-radius: 16px;
  }
  
  .dialog-header {
    padding: 20px;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .header-icon {
    margin-bottom: 12px;
  }
  
  .dialog-title {
    font-size: 1.25rem;
  }
  
  .dialog-body {
    padding: 20px;
  }
  
  .confirmation-card {
    padding: 20px;
  }
  
  .dialog-actions {
    padding: 16px 20px;
    flex-direction: column;
    gap: 12px;
  }
  
  .btn {
    width: 100%;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 12px;
  }
  
  .detail-value {
    max-width: 100%;
    text-align: left;
  }
}

/* Dark Mode */
// @media (prefers-color-scheme: dark) {
//   .dialog-content {
//     background: #1f2937;
//     box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
//   }
  
//   .confirmation-card,
//   .item-details,
//   .loading-container {
//     background: #1f2937;
//     border-color: #7f1d1d;
//   }
  
//   .confirmation-message {
//     color: #fecaca;
    
//     strong {
//       color: #fca5a5;
//     }
//   }
  
//   .detail-label {
//     color: #9ca3af;
//   }
  
//   .detail-value {
//     color: #f9fafb;
//   }
  
//   .dialog-actions {
//     background: #1f2937;
//     border-color: #7f1d1d;
//   }
  
//   .btn-secondary {
//     background: #374151;
//     color: #e5e7eb;
//     border-color: #4b5563;
  
//     &:hover:not(:disabled) {
//       background: #4b5563;
//       border-color: #6b7280;
//     }
//   }
  
//   .loading-text {
//     color: #fca5a5;
//   }
  
//   .loading-dot {
//     background: #f87171;
//   }
  
//   .item-details {
//     background: #111827;
//     border-color: #7f1d1d;
//   }
  
//   .detail-item {
//     border-bottom-color: #7f1d1d;
//   }
// }
</style>

